/**
 * JWT-BASED API CLIENT for Redd Miner Leads
 * Replace your existing api.ts with this version
 */

export interface AuthCredentials {
  username: string
  password: string
}

export interface RegisterCredentials {
  username: string
  email: string
  password: string
  first_name: string
  last_name: string
}

export interface ContactFormData {
  name: string
  email: string
  company?: string
  inquiryType: 'general' | 'sales' | 'support' | 'partnership' | 'feature'
  message: string
  // Spam protection fields
  form_timestamp?: number
  website?: string
  url?: string
  phone_number?: string
  fax?: string
}

export interface User {
  id: number
  username: string
  email: string
  display_name: string
  avatar_url: string
  roles: string[]
}

export interface Project {
  id: string
  name: string
  status: string
  subreddits: string[]
  keywords: string[]
  business_goal?: string
  goal_config?: {
    target_url?: string
    product_name?: string
    product_description?: string
    key_features?: string[]
    key_benefits?: string[]
  }
  default_template_id?: number
  created_at: string
  metrics: {
    total_opportunities: number
    new_opportunities: number
  }
  monitoring: {
    is_active: boolean
  }
}

export interface Opportunity {
  id: string
  project_id: string
  project_name: string
  title: string
  content: string
  subreddit: string
  author: string
  post_url: string
  priority: 'high' | 'medium' | 'low'
  status: 'new' | 'reviewed' | 'contacted' | 'converted' | 'archived'
  discovered_at: string
  opportunity_score: number
  post_score?: number
  num_comments?: number
  keyword_matches?: string[]
  ai_response?: {
    status: 'no_comment' | 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'posted'
    comment_id?: string
    posted_at?: string
    has_response: boolean
    comment_text?: string
    auto_generated?: boolean
    automation_mode?: 'manual' | 'review' | 'full_auto'
  }
  schedule?: {
    id: number
    scheduled_time: string
    status: 'scheduled' | 'processing' | 'completed' | 'failed'
    timing_strategy: 'optimal' | 'immediate' | 'random' | 'custom'
    priority: number
    retry_count?: number
    max_retries?: number
    error_message?: string
    executed_at?: string
  }
  engagement?: {
    upvotes: number
    downvotes: number
    score: number
    reply_count: number
    positive_replies: number
    negative_replies: number
    neutral_replies: number
    engagement_rate: number
    last_checked: string
  }
  metrics: {
    engagement_score: number
    time_since_discovery: string
    freshness: string
  }
}

export interface DashboardMetrics {
  projects: {
    total: number
    active: number
  }
  opportunities: {
    total: number
    new: number
    responded: number
    high_priority: number
  }
  performance: {
    conversion_rate: number
  }
}

export interface MonitoringStatus {
  status: 'active' | 'inactive' | 'error'
  last_run: string | null
  next_run: string | null
  frequency: string
  active_projects: number
  found_today: number
  error_message?: string
}

class JWTAPIClient {
  private baseURL: string
  private jwtAuthURL: string
  private token: string | null = null

  private isDevelopment = import.meta.env.DEV

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || 'https://admin.reddminer.com'
    this.jwtAuthURL = `${this.baseURL}/wp-json/redd-miner-leads/v1/auth`
    this.token = localStorage.getItem('rml_jwt_token')

    if (this.isDevelopment) {
      console.log('🔧 API Client initialized')
      console.log('📍 Base URL:', this.baseURL)
      console.log('🔑 JWT Auth URL:', this.jwtAuthURL)
      console.log('💾 Token from localStorage:', this.token ? this.token.substring(0, 50) + '...' : 'None')

      // Security warning for development
      if (this.token) {
        console.warn('🚨 SECURITY WARNING: JWT token stored in localStorage is vulnerable to XSS attacks')
        console.warn('📋 TODO: Implement httpOnly cookie-based authentication for production')
      }
    }
  }

  setToken(token: string) {
    this.token = token
    localStorage.setItem('rml_jwt_token', token)
  }

  clearToken() {
    this.token = null
    localStorage.removeItem('rml_jwt_token')
  }

  refreshTokenFromStorage() {
    const storedToken = localStorage.getItem('rml_jwt_token')
    if (storedToken !== this.token) {
      this.token = storedToken
      console.log('🔄 Token refreshed from localStorage:', storedToken ? storedToken.substring(0, 50) + '...' : 'None')
    }
  }

  private isTokenExpired(): boolean {
    if (!this.token) return true

    try {
      // Decode JWT payload (middle part of token)
      const parts = this.token.split('.')
      if (parts.length !== 3) return true

      const payload = JSON.parse(atob(parts[1]))

      // Check if token has expiration claim
      if (!payload.exp) {
        console.log('⚠️ Token has no expiration claim')
        return false // If no exp claim, consider token as not expired
      }

      const now = Math.floor(Date.now() / 1000) // Current time in seconds
      const expiresAt = payload.exp
      const timeUntilExpiry = expiresAt - now

      console.log(`⏰ Token expires at: ${new Date(expiresAt * 1000).toISOString()}`)
      console.log(`⏰ Current time: ${new Date(now * 1000).toISOString()}`)
      console.log(`⏰ Time until expiry: ${timeUntilExpiry} seconds`)

      return timeUntilExpiry <= 0
    } catch (error) {
      console.error('💥 Error parsing token:', error)
      return true // If we can't parse it, consider it expired
    }
  }

  private getWordPressNonce(): string | null {
    // Try to get nonce from WordPress if available
    // Check both rmlData (new) and rmlAjax (current) for backward compatibility
    if (typeof window !== 'undefined') {
      const windowObj = window as any

      // Try rmlData first (preferred)
      if (windowObj.rmlData?.nonce) {
        console.log('🔑 Using nonce from rmlData.nonce')
        return windowObj.rmlData.nonce
      }

      // Fallback to rmlAjax.restNonce (currently used in production)
      if (windowObj.rmlAjax?.restNonce) {
        console.log('🔑 Using nonce from rmlAjax.restNonce')
        return windowObj.rmlAjax.restNonce
      }

      console.warn('⚠️ No WordPress nonce found in rmlData or rmlAjax')
    }

    // For standalone React app, we don't have WordPress nonce
    // The monitoring endpoint should allow JWT authentication
    return null
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`

    // Always refresh token from localStorage before making requests
    // This ensures we have the latest token after OAuth flows
    this.refreshTokenFromStorage()

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    // Try JWT first for all authenticated endpoints
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }

    // For WordPress REST API endpoints, also try WordPress nonce as fallback
    if (endpoint.startsWith('/wp-json/')) {
      const nonce = this.getWordPressNonce()
      if (nonce) {
        headers['X-WP-Nonce'] = nonce
      }

      // Also try Authorization header with different formats
      if (this.token && headers.Authorization && !headers.Authorization.startsWith('Bearer')) {
        headers.Authorization = `Bearer ${this.token}`
      }
    }

    if (this.isDevelopment) {
      console.log(`🌐 API Request: ${options.method || 'GET'} ${url}`)
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      })

      if (this.isDevelopment) {
        console.log(`📨 Response: ${response.status}`)
      }

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ Error response:', errorText)

        let errorData
        try {
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { error: errorText }
        }

        if (response.status === 401) {
          // Token expired or invalid - try to handle gracefully
          console.log('🔄 401 Unauthorized - checking token expiration')

          // Parse token to check if it's expired vs invalid
          if (this.token) {
            const isExpired = this.isTokenExpired()
            if (isExpired) {
              console.log('⏰ Token is expired, clearing token and requiring re-authentication')
              this.clearToken()
              throw new Error('Token expired - please log in again')
            } else {
              console.log('🔒 Token appears valid but authentication failed - may be server-side issue')
              // Don't clear token immediately, let user try again
              throw new Error(errorData.error || 'Authentication failed - please try again')
            }
          } else {
            console.log('🚫 No token present for 401 error')
            throw new Error('Authentication required - please log in')
          }
        }
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('💥 API request failed:', error)
      throw error
    }
  }

  // Authentication
  async login(credentials: AuthCredentials): Promise<User> {
    try {
      const response = await this.request<{
        success: boolean
        message: string
        data: {
          jwt_token: string
          expires: number
          user: User & { activation_required?: boolean }
        }
      }>(`${this.jwtAuthURL}/login`, {
        method: 'POST',
        body: JSON.stringify(credentials),
      })

      if (!response.success) {
        throw new Error(response.message || 'Login failed')
      }

      // Store JWT token
      this.setToken(response.data.jwt_token)

      // Store activation status
      if (response.data.user.activation_required) {
        localStorage.setItem('rml_activation_required', 'true')
      } else {
        localStorage.removeItem('rml_activation_required')
      }

      return response.data.user
    } catch (error) {
      this.clearToken()
      console.error('❌ JWT login failed:', error)
      throw error
    }
  }

  async register(credentials: RegisterCredentials): Promise<User> {

    try {
      const response = await this.request<{
        success: boolean
        message: string
        data: {
          jwt_token: string
          expires: number
          user: User & { activation_required?: boolean }
        }
      }>(`${this.jwtAuthURL}/register`, {
        method: 'POST',
        body: JSON.stringify(credentials),
      })

      if (!response.success) {
        throw new Error(response.message || 'Registration failed')
      }

      // Store JWT token (automatic login after registration)
      this.setToken(response.data.jwt_token)

      // Store activation status
      if (response.data.user.activation_required) {
        localStorage.setItem('rml_activation_required', 'true')
      } else {
        localStorage.removeItem('rml_activation_required')
      }

      console.log('🎉 Registration successful!')
      console.log('🔑 Auto-logged in, token expires at:', new Date(response.data.expires * 1000))
      if (response.data.user.activation_required) {
        console.log('⚠️ Please check your email to activate your account')
      }

      return response.data.user
    } catch (error) {
      this.clearToken()
      console.error('❌ Registration failed:', error)
      throw error
    }
  }

  async getCurrentUser(): Promise<User> {
    console.log('👤 Verifying JWT token...')

    // Refresh token from localStorage in case it was updated during OAuth
    this.refreshTokenFromStorage()

    if (!this.token) {
      throw new Error('No token available')
    }

    try {
      const response = await this.request<{
        success: boolean
        data: {
          user: User & { activation_required?: boolean }
          token_data: any
        }
      }>(`${this.jwtAuthURL}/verify`)

      if (!response.success) {
        throw new Error('Token verification failed')
      }

      // Store activation status
      if (response.data.user.activation_required) {
        localStorage.setItem('rml_activation_required', 'true')
      } else {
        localStorage.removeItem('rml_activation_required')
      }

      console.log('✅ JWT token verified')
      console.log('👤 User:', response.data.user.username)

      return response.data.user
    } catch (error) {
      console.error('❌ JWT verification failed:', error)
      this.clearToken()
      throw error
    }
  }

  logout() {
    console.log('👋 Logging out...')
    this.clearToken()
    localStorage.removeItem('rml_activation_required')
  }

  // Activate user account
  async activateAccount(token: string): Promise<User> {
    console.log('✅ Activating account...')

    try {
      const response = await this.request<{
        success: boolean
        message: string
        data: {
          jwt_token: string
          expires: number
          user: User
        }
      }>(`${this.jwtAuthURL}/activate`, {
        method: 'POST',
        body: JSON.stringify({ token }),
      })

      if (!response.success) {
        throw new Error(response.message || 'Activation failed')
      }

      // Store new JWT token
      this.setToken(response.data.jwt_token)

      // Remove activation requirement
      localStorage.removeItem('rml_activation_required')

      console.log('🎉 Account activated successfully!')
      console.log('🔑 New token expires at:', new Date(response.data.expires * 1000))

      return response.data.user
    } catch (error) {
      console.error('❌ Activation failed:', error)
      throw error
    }
  }

  // Resend activation email
  async resendActivation(email: string): Promise<void> {
    console.log('📧 Resending activation email...')

    try {
      const response = await this.request<{
        success: boolean
        message: string
      }>(`${this.jwtAuthURL}/resend-activation`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      })

      if (!response.success) {
        throw new Error(response.message || 'Failed to resend activation email')
      }

      console.log('✅ Activation email sent')
    } catch (error) {
      console.error('❌ Failed to resend activation email:', error)
      throw error
    }
  }

  // Check if activation is required
  isActivationRequired(): boolean {
    return localStorage.getItem('rml_activation_required') === 'true'
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.token
  }

  // Get current token (for debugging)
  getToken(): string | null {
    return this.token
  }


  // Set WordPress nonce (called after successful authentication)
  setWordPressNonce(nonce: string) {
    localStorage.setItem('rml_wp_nonce', nonce)
  }

  // Generate a basic nonce as fallback
  private generateBasicNonce(): string {
    // WARNING: This is not cryptographically secure and should be replaced
    // with proper WordPress nonce generation in production
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('🚨 Using fallback nonce - not secure for production')
    }
    return 'wp_rest_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36)
  }

  // Dashboard
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    try {
      const response = await this.request<{ success: boolean; data: DashboardMetrics }>(
        '/wp-json/redd-miner-leads/v1/dashboard/metrics'
      )
      return response.data
    } catch (error) {
      console.warn('Dashboard API endpoint not available, calculating from projects and opportunities:', error)

      // Get real data from existing endpoints
      try {
        console.log('🔄 Fetching projects and opportunities for dashboard metrics...')

        // Calculate 24 hours ago timestamp for backend filtering
        const twentyFourHoursAgo = new Date()
        twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24)
        const twentyFourHoursAgoISO = twentyFourHoursAgo.toISOString()

        const [projects, allOpportunities, recentOpportunitiesResponse, respondedOpportunitiesResponse] = await Promise.all([
          this.getProjects(),
          this.getOpportunities({ per_page: 100 }), // Backend limits to 100, but we use pagination.total_count
          this.getOpportunities({ discovered_after: twentyFourHoursAgoISO, per_page: 1 }), // Get count for opportunities in last 24h
          this.getOpportunities({ status: 'contacted', per_page: 1 }) // Get count for responded opportunities
        ])

        console.log('📊 Raw data received:', { projects, allOpportunities, recentOpportunitiesResponse, respondedOpportunitiesResponse })

        // Ensure projects is an array
        const projectsArray = Array.isArray(projects) ? projects : []
        console.log('✅ Projects array:', projectsArray.length, 'items')

        // Use pagination total counts for accurate metrics
        const activeProjects = projectsArray.filter(p => p.status === 'monitoring').length
        const totalOpportunities = allOpportunities?.pagination?.total_count || 0
        const recentOpportunities = recentOpportunitiesResponse?.pagination?.total_count || 0
        const respondedOpportunities = respondedOpportunitiesResponse?.pagination?.total_count || 0

        // For high priority, we still need to use filtering since there's no direct API filter
        const opportunitiesArray = Array.isArray(allOpportunities?.opportunities) ? allOpportunities.opportunities : []
        const highPriorityOpportunities = opportunitiesArray.filter(o => o.priority === 'high').length

        console.log('📈 Calculated metrics:', {
          totalProjects: projectsArray.length,
          activeProjects,
          totalOpportunities,
          recentOpportunities,
          respondedOpportunities,
          highPriorityOpportunities
        })

        return {
          projects: {
            total: projectsArray.length,
            active: activeProjects
          },
          opportunities: {
            total: totalOpportunities,
            new: recentOpportunities, // This is now opportunities in last 24h
            responded: respondedOpportunities,
            high_priority: highPriorityOpportunities
          },
          performance: {
            conversion_rate: totalOpportunities > 0 ? Math.round((respondedOpportunities / totalOpportunities) * 100 * 10) / 10 : 0
          }
        }
      } catch (fallbackError) {
        console.warn('Fallback metrics calculation failed:', fallbackError)
        return {
          projects: { total: 0, active: 0 },
          opportunities: { total: 0, new: 0, responded: 0, high_priority: 0 },
          performance: { conversion_rate: 0 }
        }
      }
    }
  }

  // Monitoring Status
  async getMonitoringStatus(): Promise<MonitoringStatus> {
    try {
      const response = await this.request<{ success: boolean; data: MonitoringStatus }>(
        '/wp-json/redd-miner-leads/v1/monitoring/status'
      )
      return response.data
    } catch (error) {
      console.warn('Monitoring status API endpoint not available, using fallback:', error)

      // Fallback: calculate basic status from projects
      try {
        const projects = await this.getProjects()
        const projectsArray = Array.isArray(projects) ? projects : []
        const activeProjects = projectsArray.filter(p => p.status === 'monitoring').length

        // Calculate opportunities found today
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const todayISO = today.toISOString()

        const todayOpportunities = await this.getOpportunities({
          discovered_after: todayISO,
          per_page: 1
        })

        return {
          status: activeProjects > 0 ? 'active' : 'inactive',
          last_run: null,
          next_run: null,
          frequency: 'Every 5 minutes',
          active_projects: activeProjects,
          found_today: todayOpportunities.pagination?.total_count || 0,
          error_message: activeProjects === 0 ? 'No projects are currently being monitored' : undefined
        }
      } catch (fallbackError) {
        console.warn('Fallback monitoring status calculation failed:', fallbackError)
        return {
          status: 'error',
          last_run: null,
          next_run: null,
          frequency: 'Unknown',
          active_projects: 0,
          found_today: 0,
          error_message: 'Unable to determine monitoring status'
        }
      }
    }
  }

  // Projects
  async getProjects(): Promise<Project[]> {
    try {
      // Use the basic projects endpoint directly
      const response = await this.request<{ success: boolean; data: any }>(
        '/wp-json/redd-miner-leads/v1/projects'
      )

      console.log('🔍 Projects API response structure:', response)
      console.log('🔍 Projects data object:', response.data)

      // Handle different response structures
      let projects: Project[] = []

      if (Array.isArray(response.data)) {
        // Direct array: {success: true, data: [...]}
        projects = response.data
        console.log('📝 Using direct array structure')
      } else if (response.data && Array.isArray(response.data.projects)) {
        // Nested array: {success: true, data: {projects: [...]}}
        projects = response.data.projects
        console.log('📝 Using nested projects array structure')
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Double nested: {success: true, data: {data: [...]}}
        projects = response.data.data
        console.log('📝 Using double nested data structure')
      } else if (response.data && typeof response.data === 'object') {
        // Try to find any array in the data object
        console.log('📝 Searching for arrays in data object...')
        Object.keys(response.data).forEach(key => {
          console.log(`🔍 Found key "${key}":`, Array.isArray(response.data[key]) ? `Array(${response.data[key].length})` : typeof response.data[key])
          if (Array.isArray(response.data[key]) && projects.length === 0) {
            projects = response.data[key]
            console.log(`📝 Using array from key "${key}"`)
          }
        })
      }

      console.log('✅ Parsed projects:', projects.length, 'items')
      console.log('🔍 Sample project object structure:', projects[0])

      // Log all properties of the first project to understand the structure
      if (projects.length > 0) {
        console.log('📋 Available project properties:', Object.keys(projects[0]))
      }

      // Transform WordPress API data structure to match frontend expectations
      const transformedProjects = projects.map((project: any) => {
          console.log('🔧 Transforming project:', project.name)
          console.log('📄 Raw project data:', project)

          // Parse subreddits - handle multiple formats
          let subreddits: string[] = []

          // Try project.subreddits first (if already transformed)
          if (Array.isArray(project.subreddits)) {
            subreddits = project.subreddits
            console.log('✅ Using existing subreddits array:', subreddits)
          }
          // Try project.target_subreddits (WordPress API field)
          else if (project.target_subreddits) {
            if (Array.isArray(project.target_subreddits)) {
              // Already an array
              subreddits = project.target_subreddits
              console.log('✅ target_subreddits is array:', subreddits)
            } else if (typeof project.target_subreddits === 'string') {
              try {
                // Try to parse as JSON first
                const parsed = JSON.parse(project.target_subreddits)
                subreddits = Array.isArray(parsed) ? parsed : []
                console.log('✅ Parsed target_subreddits JSON:', subreddits)
              } catch (e) {
                // If not JSON, treat as comma-separated
                subreddits = project.target_subreddits.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
                console.log('✅ Parsed target_subreddits CSV:', subreddits)
              }
            }
          }

          // Parse keywords - handle multiple formats
          let keywords: string[] = []

          if (Array.isArray(project.keywords)) {
            // Already an array
            keywords = project.keywords
            console.log('✅ Using existing keywords array:', keywords)
          } else if (project.keywords && typeof project.keywords === 'string') {
            // Check if it's JSON array format like ["keyword1","keyword2"]
            if (project.keywords.trim().startsWith('[') && project.keywords.trim().endsWith(']')) {
              try {
                const parsed = JSON.parse(project.keywords)
                keywords = Array.isArray(parsed) ? parsed : []
                console.log('✅ Parsed keywords JSON:', keywords)
              } catch (e) {
                // If JSON parsing fails, fall back to comma-separated
                keywords = project.keywords.split(',').map((k: string) => k.trim()).filter((k: string) => k.length > 0)
                console.log('✅ Fallback keywords CSV:', keywords)
              }
            } else {
              // Regular comma-separated string
              keywords = project.keywords.split(',').map((k: string) => k.trim()).filter((k: string) => k.length > 0)
              console.log('✅ Parsed keywords CSV:', keywords)
            }
          }

          // Parse goal_config - handle both object and JSON string formats
          let parsedGoalConfig = project.goal_config
          if (typeof project.goal_config === 'string' && project.goal_config.trim().startsWith('{')) {
            try {
              parsedGoalConfig = JSON.parse(project.goal_config)
              console.log('✅ Parsed goal_config from JSON string:', parsedGoalConfig)
            } catch (e) {
              console.warn('⚠️ Failed to parse goal_config JSON:', e)
              parsedGoalConfig = null
            }
          }

          // Create transformed project object
          const transformedProject: Project = {
            id: project.id,
            name: project.name,
            status: project.status,
            subreddits,
            keywords,
            business_goal: project.business_goal,
            goal_config: parsedGoalConfig, // Parsed goal_config (object or null)
            default_template_id: project.default_template_id,
            created_at: project.created_at,
            metrics: {
              total_opportunities: 0, // TODO: Fetch actual opportunity count
              new_opportunities: 0
            },
            monitoring: {
              is_active: project.status === 'monitoring'
            }
          }

          console.log('✅ Transformed project:', transformedProject.name, {
            subreddits: transformedProject.subreddits.length,
            keywords: transformedProject.keywords.length,
            status: transformedProject.status,
            hasGoalConfig: !!transformedProject.goal_config
          })

          return transformedProject
        })

        console.log('🎉 All projects transformed successfully!')

        // Fetch opportunity counts for each project
        console.log('📊 Fetching opportunity counts for projects...')
        try {
          const projectsWithMetrics = await Promise.all(
            transformedProjects.map(async (project) => {
              try {
                // Get opportunities for this specific project
                const opportunities = await this.getOpportunities({
                  project_id: project.id,
                  per_page: 100 // Backend limits to 100, but we use pagination.total_count
                })

                // Use pagination total count instead of array length for accurate count
                const totalOpportunities = opportunities.pagination?.total_count || 0

                // For new opportunities, we need a separate API call to get the accurate count
                const newOpportunitiesResponse = await this.getOpportunities({
                  project_id: project.id,
                  status: 'new',
                  per_page: 1 // We only need the count, not the data
                })
                const newOpportunities = newOpportunitiesResponse.pagination?.total_count || 0

                console.log(`📊 Project "${project.name}": ${totalOpportunities} total, ${newOpportunities} new`)

                return {
                  ...project,
                  metrics: {
                    total_opportunities: totalOpportunities,
                    new_opportunities: newOpportunities
                  }
                }
              } catch (error) {
                console.warn(`⚠️ Failed to fetch opportunities for project ${project.name}:`, error)
                return project // Return original project with 0 metrics if fetch fails
              }
            })
          )

          console.log('✅ Enhanced projects with opportunity metrics!')
          console.log('📊 Total projects:', projectsWithMetrics.length)
          console.log('📊 Project details:', projectsWithMetrics.map(p => `ID:${p.id} Name:"${p.name}" Status:${p.status}`))

          return projectsWithMetrics
        } catch (error) {
          console.warn('⚠️ Failed to enhance projects with metrics, returning without metrics:', error)
          return transformedProjects
        }
    } catch (error) {
      console.error('Projects API failed:', error)
      throw new Error('Failed to load projects from API')
    }
  }

  async createProject(project: Partial<Project>): Promise<Project> {
    console.log('🔧 API: Creating project with data:', project)
    const response = await this.request<{ success: boolean; data: Project }>(
      '/wp-json/redd-miner-leads/v1/projects',
      {
        method: 'POST',
        body: JSON.stringify(project),
      }
    )
    console.log('✅ API: Create project response:', response)
    return response.data
  }

  async updateProject(id: string, updates: Partial<Project>): Promise<Project> {
    console.log('🔧 API: Updating project', id, 'with data:', updates)
    console.log('📊 API: Update details:', {
      subreddits: (updates as any).subreddits,
      target_subreddits: (updates as any).target_subreddits,
      keywords: updates.keywords
    })

    const response = await this.request<{ success: boolean; data: Project }>(
      `/wp-json/redd-miner-leads/v1/projects/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    )
    console.log('✅ API: Update project response:', response)
    return response.data
  }

  async deleteProject(id: string): Promise<void> {
    await this.request(`/wp-json/redd-miner-leads/v1/projects/${id}`, {
      method: 'DELETE',
    })
  }

  // Preview AI Prompt before generation
  async previewPrompt(
    opportunityId: string,
    projectId: string,
    templateId?: number,
    customContext?: string
  ): Promise<{
    success: boolean
    data: {
      input_data: {
        opportunity: {
          id: number
          title: string
          content: string
          content_full: string
          subreddit: string
          author: string
          post_url: string
        }
        project: {
          id: number
          name: string
          business_goal: string
          target_url: string | null
          product_description: string | null
          tone: string | null
          promotional_percentage: number
        }
        template: {
          id: number
          name: string
          tone: string
          style: string
          category: string
          template_text: string
        } | null
        custom_context: string
      }
      prompts: {
        system_prompt: string
        user_prompt: string
        full_prompt: string
      }
      ai_settings: {
        provider: string
        fallback_provider: string
        model: string
        max_tokens: number
        temperature: number
        presence_penalty: number
        frequency_penalty: number
        estimated_cost: number
      }
      metadata: {
        prompt_tokens_estimate: number
        system_tokens: number
        total_tokens_estimate: number
        estimated_cost: number
      }
    }
  }> {
    const params = new URLSearchParams({
      opportunity_id: opportunityId,
      project_id: projectId
    })

    if (templateId) {
      params.append('template_id', templateId.toString())
    }

    if (customContext) {
      params.append('custom_context', customContext)
    }

    const response = await this.request<{
      success: boolean
      data: any
    }>(`/wp-json/redd-miner-leads/v1/comments/preview-prompt?${params.toString()}`, {
      method: 'GET'
    })

    return response
  }

  // AI Comment Generation
  async generateAIComment(
    opportunityId: string,
    templateId?: number,
    customContext?: string
  ): Promise<{
    success: boolean
    data: {
      comment: string
      quality_score: number
      tokens_used: number
      template_used?: {
        id: number
        name: string
        category: string
      }
    }
  }> {
    const body: any = {}
    if (templateId) {
      body.template_id = templateId
    }
    if (customContext) {
      body.custom_context = customContext
    }

    const response = await this.request<{
      success: boolean
      data: {
        comment: string
        quality_score: number
        tokens_used: number
        template_used?: {
          id: number
          name: string
          category: string
        }
      }
    }>(`/wp-json/redd-miner-leads/v1/opportunities/${opportunityId}/generate-comment`, {
      method: 'POST',
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
    })

    return response
  }

  // Get available response templates
  async getResponseTemplates(): Promise<{
    success: boolean
    data: Array<{
      id: number
      name: string
      description: string
      template_text: string
      category: string
      goal_type: string | null
      tone: string
      style: string
      use_count: number
      last_used_at: string | null
      is_public: boolean
      user_id: number
    }>
  }> {
    return this.get('/intelligence/templates')
  }

  // Post comment to Reddit
  async postCommentToReddit(opportunityId: string, comment: string): Promise<{
    success: boolean
    message: string
    data?: {
      reddit_comment_id: string
      reddit_comment_url: string
    }
  }> {
    const response = await this.request<{
      success: boolean
      message: string
      data?: {
        reddit_comment_id: string
        reddit_comment_url: string
      }
    }>(`/wp-json/redd-miner-leads/v1/opportunities/${opportunityId}/post-to-reddit`, {
      method: 'POST',
      body: JSON.stringify({
        comment: comment
      })
    })

    return response
  }

  // Opportunities
  async getOpportunities(params: {
    page?: number
    per_page?: number
    status?: string
    priority?: string
    project_id?: string
    search?: string
    filter_ai_status?: string
    filter_keyword?: string
    discovered_after?: string
  } = {}): Promise<{
    opportunities: Opportunity[]
    pagination: {
      total_count: number
      page: number
      per_page: number
      total_pages: number
    }
  }> {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, value.toString())
      }
    })

    const response = await this.request<{
      success: boolean
      data: any
    }>(`/wp-json/redd-miner-leads/v1/opportunities?${searchParams}`)

    console.log('🔍 Opportunities API response structure:', response)

    // Handle different response structures
    if (response.data && response.data.opportunities) {
      // Expected structure: {success: true, data: {opportunities: [...], pagination: {...}}}
      console.log('✅ Found opportunities array:', response.data.opportunities.length, 'items')
      return response.data
    } else if (Array.isArray(response.data)) {
      // Direct array structure: {success: true, data: [...], total: X, page: Y, per_page: Z}
      console.log('✅ Found direct opportunities array:', response.data.length, 'items')

      // Extract pagination data from root response object
      const totalCount = response.total || response.data.length
      const currentPage = response.page || 1
      const perPageCount = response.per_page || response.data.length
      const totalPages = Math.ceil(totalCount / perPageCount)

      console.log('🔧 PAGINATION FIX - Calculated pagination:', {
        totalCount,
        currentPage,
        perPageCount,
        totalPages
      })

      return {
        opportunities: response.data,
        pagination: {
          total_count: totalCount,
          page: currentPage,
          per_page: perPageCount,
          total_pages: totalPages
        }
      }
    } else {
      console.warn('⚠️ Unexpected opportunities response structure:', response.data)
      return {
        opportunities: [],
        pagination: {
          total_count: 0,
          page: 1,
          per_page: 0,
          total_pages: 0
        }
      }
    }
  }

  // Get opportunity metrics by fetching all opportunities with current filters (no pagination)
  async getOpportunityMetrics(params: {
    status?: string
    priority?: string
    project_id?: string
    search?: string
  } = {}): Promise<{
    total: number
    new: number
    reviewed: number
    contacted: number
    converted: number
    archived: number
    high_priority: number
    medium_priority: number
    low_priority: number
    ai_generated: number
    ai_posted: number
  }> {
    // Fetch all opportunities (no pagination) with current filters to calculate metrics
    const allOpportunities = await this.getOpportunities({
      ...params,
      per_page: 10000, // Large number to get all opportunities
      page: 1
    })

    const opportunities = allOpportunities.opportunities || []

    console.log('📊 METRICS DEBUG - Calculating from', opportunities.length, 'opportunities')

    return {
      total: allOpportunities.pagination?.total_count || opportunities.length,
      new: opportunities.filter(opp => opp.status === 'new').length,
      reviewed: opportunities.filter(opp => opp.status === 'reviewed').length,
      contacted: opportunities.filter(opp => opp.status === 'contacted').length,
      converted: opportunities.filter(opp => opp.status === 'converted').length,
      archived: opportunities.filter(opp => opp.status === 'archived').length,
      high_priority: opportunities.filter(opp => opp.priority === 'high').length,
      medium_priority: opportunities.filter(opp => opp.priority === 'medium').length,
      low_priority: opportunities.filter(opp => opp.priority === 'low').length,
      ai_generated: opportunities.filter(opp => opp.ai_response?.has_response).length,
      ai_posted: opportunities.filter(opp => opp.ai_response?.status === 'posted').length,
    }
  }

  // System Status
  async getSystemStatus(): Promise<{
    database: boolean
    tables: boolean
    graphql: boolean
    version: string
    environment: string
  }> {
    const response = await this.request<{
      success: boolean
      data: {
        database: boolean
        tables: boolean
        graphql: boolean
        version: string
        environment: string
      }
    }>('/wp-json/redd-miner-leads/v1/system/status')

    return response.data
  }

  // Health check (no auth required)
  async healthCheck(): Promise<boolean> {
    try {
      await this.request('/wp-json/wp/v2/')
      return true
    } catch {
      return false
    }
  }

  // Token refresh (if needed in the future)
  async refreshToken(): Promise<boolean> {
    // For now, just check if current token is still valid
    try {
      await this.getCurrentUser()
      return true
    } catch {
      return false
    }
  }

  // Contact Form
  async sendContactForm(data: ContactFormData): Promise<{
    success: boolean
    message: string
    data?: {
      timestamp: string
      inquiry_type: string
    }
  }> {
    console.log('📧 Sending contact form submission...')

    try {
      const response = await this.request<{
        success: boolean
        message: string
        data?: {
          timestamp: string
          inquiry_type: string
        }
      }>('/wp-json/redd-miner-leads/v1/contact', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      console.log('✅ Contact form sent successfully!')
      return response
    } catch (error) {
      console.error('❌ Contact form submission failed:', error)
      throw error
    }
  }

  // HTTP convenience methods with enhanced Intelligence debugging
  async get(endpoint: string): Promise<any> {
    return this.request(`/wp-json/redd-miner-leads/v1${endpoint}`, {
      method: 'GET'
    })
  }

  async post(endpoint: string, data?: any): Promise<any> {
    return this.request(`/wp-json/redd-miner-leads/v1${endpoint}`, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async put(endpoint: string, data?: any): Promise<any> {
    return this.request(`/wp-json/redd-miner-leads/v1${endpoint}`, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async delete(endpoint: string): Promise<any> {
    return this.request(`/wp-json/redd-miner-leads/v1${endpoint}`, {
      method: 'DELETE'
    })
  }

  // ==================== PHASE 2 PRIORITY 3: SCHEDULING ====================

  /**
   * Preview optimal schedule time for an opportunity
   */
  async previewSchedule(opportunityId: string, strategy: 'optimal' | 'immediate' | 'random' = 'optimal'): Promise<{
    success: boolean
    data: {
      scheduled_time: number
      scheduled_time_formatted: string
      time_until: string
      strategy: string
      subreddit: string
    }
  }> {
    return this.get(`/schedule/preview?opportunity_id=${opportunityId}&strategy=${strategy}`)
  }

  /**
   * Schedule a comment for posting
   */
  async scheduleComment(data: {
    opportunity_id: number
    comment_id: number
    strategy: 'optimal' | 'immediate' | 'random' | 'custom'
    custom_time?: number
  }): Promise<{
    success: boolean
    data: {
      schedule_id: number
      scheduled_time: number
      scheduled_time_formatted: string
      time_until: string
      strategy: string
    }
  }> {
    return this.post('/schedule/create', data)
  }

  /**
   * Get list of scheduled activities
   */
  async getScheduledActivities(filters?: {
    status?: string
    project_id?: number
    from_date?: string
    to_date?: string
  }): Promise<{
    success: boolean
    data: {
      activities: Array<{
        id: number
        opportunity_id: number
        comment_id: number
        scheduled_time: string
        status: string
        timing_strategy: string
        subreddit: string
        title: string
        project_name: string
      }>
      total: number
    }
  }> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.project_id) params.append('project_id', filters.project_id.toString())
    if (filters?.from_date) params.append('from_date', filters.from_date)
    if (filters?.to_date) params.append('to_date', filters.to_date)

    const query = params.toString()
    return this.get(`/schedule/list${query ? '?' + query : ''}`)
  }

  /**
   * Cancel a scheduled activity
   */
  async cancelSchedule(scheduleId: number): Promise<{
    success: boolean
    message: string
  }> {
    return this.delete(`/schedule/${scheduleId}/cancel`)
  }

  /**
   * Reschedule an activity to a new time
   */
  async rescheduleActivity(scheduleId: number, newTime: number | string): Promise<{
    success: boolean
    message: string
  }> {
    // If newTime is a string (YYYY-MM-DD HH:MM), send as scheduled_datetime to avoid timezone conversion
    // If newTime is a number (Unix timestamp), send as scheduled_time for backwards compatibility
    if (typeof newTime === 'string') {
      return this.put(`/schedule/${scheduleId}/update`, {
        scheduled_datetime: newTime
      })
    } else {
      return this.put(`/schedule/${scheduleId}/update`, {
        scheduled_time: newTime
      })
    }
  }

  /**
   * Get scheduling rules
   */
  async getSchedulingRules(projectId?: number, subreddit?: string): Promise<{
    success: boolean
    data: {
      active_hours_start: string
      active_hours_end: string
      timezone: string
      active_days: string
      min_delay_minutes: number
      max_delay_minutes: number
      min_interval_between_posts: number
      randomize_timing: boolean
      simulate_breaks: boolean
      break_probability: number
      break_duration_min: number
      break_duration_max: number
      max_posts_per_hour: number
      max_posts_per_day: number
      default_strategy: string
    }
  }> {
    const params = new URLSearchParams()
    if (projectId) params.append('project_id', projectId.toString())
    if (subreddit) params.append('subreddit', subreddit)

    const query = params.toString()
    return this.get(`/schedule/rules${query ? '?' + query : ''}`)
  }

  /**
   * Update scheduling rules
   */
  async updateSchedulingRules(rules: any, projectId?: number, subreddit?: string): Promise<{
    success: boolean
    message: string
  }> {
    const data = { ...rules }
    if (projectId) data.project_id = projectId
    if (subreddit) data.subreddit = subreddit

    return this.put('/schedule/rules', data)
  }

  /**
   * Get list of users (admin only)
   */
  async getUsersList(): Promise<{
    success: boolean
    users: Array<{
      id: number
      email: string
      display_name: string
      username: string
      roles: string[]
    }>
  }> {
    return this.get('/users/list')
  }

  /**
   * Log user switch event (admin only)
   */
  async logUserSwitch(targetUserId: number): Promise<{
    success: boolean
    message: string
  }> {
    return this.post('/users/switch', { target_user_id: targetUserId })
  }

  // ==================== AUTOMATION API METHODS ====================

  /**
   * Get project automation configuration
   */
  async getAutomationConfig(projectId: number): Promise<{
    success: boolean
    data: {
      automation_level: 'manual' | 'review' | 'full_auto'
      automation_paused: boolean
      automation_config: {
        max_posts_per_subreddit_per_day: number
        auto_mode_daily_threshold: number
        review_mode_daily_threshold: number
        consecutive_error_threshold: number
        quality_score_minimum: number
        notification_preferences: {
          email_on_approval_needed: boolean
          email_on_error: boolean
          daily_digest: boolean
        }
      }
    }
  }> {
    return this.get(`/projects/${projectId}/automation`)
  }

  /**
   * Toggle automation level for a project
   */
  async toggleAutomation(projectId: number, level: 'manual' | 'review' | 'full_auto'): Promise<{
    success: boolean
    data: {
      automation_level: string
      automation_paused: boolean
    }
    message: string
  }> {
    return this.post(`/projects/${projectId}/automation/toggle`, {
      automation_level: level
    })
  }

  /**
   * Update automation configuration for a project
   */
  async updateAutomationConfig(projectId: number, config: {
    max_posts_per_subreddit_per_day?: number
    auto_mode_daily_threshold?: number
    review_mode_daily_threshold?: number
    consecutive_error_threshold?: number
    quality_score_minimum?: number
    notification_preferences?: {
      email_on_approval_needed?: boolean
      email_on_error?: boolean
      daily_digest?: boolean
    }
  }): Promise<{
    success: boolean
    message: string
  }> {
    return this.put(`/projects/${projectId}/automation`, {
      config
    })
  }

  /**
   * Get pending approval comments
   */
  async getPendingApprovals(filters?: {
    project_id?: number
    page?: number
    per_page?: number
  }): Promise<{
    success: boolean
    data: {
      items: Array<{
        id: number
        opportunity_id: number
        content: string
        created_at: string
        opportunity_title: string
        subreddit: string
        post_url: string
        project_name: string
        project_id: number
      }>
      total: number
      page: number
      per_page: number
      total_pages: number
    }
  }> {
    const params = new URLSearchParams()
    if (filters?.project_id) params.append('project_id', filters.project_id.toString())
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.per_page) params.append('per_page', filters.per_page.toString())

    const query = params.toString()
    return this.get(`/comments/pending-approval${query ? '?' + query : ''}`)
  }

  /**
   * Approve a comment and schedule it
   */
  async approveComment(commentId: number, timingStrategy: 'optimal' | 'immediate' | 'random' | 'custom' = 'optimal', customTime?: string): Promise<{
    success: boolean
    data: {
      comment_id: number
      schedule_id: number
      scheduled_time: string
    }
    message: string
  }> {
    const data: any = { timing_strategy: timingStrategy }
    if (customTime) data.custom_time = customTime

    return this.post(`/comments/${commentId}/approve`, data)
  }

  /**
   * Reject a comment
   */
  async rejectComment(commentId: number, reason?: string): Promise<{
    success: boolean
    message: string
  }> {
    return this.post(`/comments/${commentId}/reject`, {
      reason
    })
  }

  /**
   * Get automation logs
   */
  async getAutomationLogs(filters?: {
    project_id?: number
    action?: string
    status?: 'success' | 'error' | 'pending'
    from_date?: string
    to_date?: string
    page?: number
    per_page?: number
  }): Promise<{
    success: boolean
    data: Array<{
      id: number
      project_id: number
      opportunity_id: number
      action: string
      status: string
      created_at: string
      details: any
    }>
    pagination: {
      total: number
      page: number
      per_page: number
    }
  }> {
    const params = new URLSearchParams()
    if (filters?.project_id) params.append('project_id', filters.project_id.toString())
    if (filters?.action) params.append('action', filters.action)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.from_date) params.append('from_date', filters.from_date)
    if (filters?.to_date) params.append('to_date', filters.to_date)
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.per_page) params.append('per_page', filters.per_page.toString())

    const query = params.toString()
    return this.get(`/automation/logs${query ? '?' + query : ''}`)
  }

  /**
   * Get automation analytics/stats
   */
  async getAutomationStats(projectId?: number): Promise<{
    success: boolean
    data: {
      events_24h: number
      pending_approvals: number
      auto_posted_24h: number
      error_rate: number
      by_project?: Array<{
        project_id: number
        project_name: string
        automation_level: string
        events_count: number
        success_count: number
        error_count: number
      }>
    }
  }> {
    const params = projectId ? `?project_id=${projectId}` : ''
    return this.get(`/automation/stats${params}`)
  }
}

export const apiClient = new JWTAPIClient()
export default apiClient

// Export for debugging
export { JWTAPIClient }